import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Button, Space, Select, Row, Col, Card, Carousel, Image } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Slider from "../components/base/Slider";
import Newsletter from "../components/Newsletter";
import { publicBlogService, advertisementService } from "../services/publicApi";

const Home = () => {
  const navigate = useNavigate();
  
  const [search, setSearch] = useState('');
  const [homeCategory, setHomeCategory] = useState();
  const [blogs, setBlogs] = useState([]);
  const [advertisements, setAdvertisements] = useState([]);

  const toEmbedUrl = (raw) => {
    if (!raw) return '';
    let url = String(raw).trim();
    if (/^www\./i.test(url)) url = `https://${url}`;
    if (/^(youtube\.com|m\.youtube\.com|youtu\.be)/i.test(url)) url = `https://${url}`;
    try {
      const u = new URL(url);
      // YouTube: watch?v=ID
      if ((/youtube\.com$/i.test(u.hostname) || /m\.youtube\.com$/i.test(u.hostname) || /youtu\.be$/i.test(u.hostname) || /youtube-nocookie\.com$/i.test(u.hostname))) {
        // youtu.be/ID
        if (u.hostname.includes('youtu.be')) {
          const id = u.pathname.replace('/', '');
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        // shorts/ID
        if (u.pathname.startsWith('/shorts/')) {
          const id = u.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        // watch?v=ID
        const idParam = u.searchParams.get('v');
        if (idParam) return `https://www.youtube.com/embed/${idParam}`;
        // already embed
        if (u.pathname.startsWith('/embed/')) {
          return `https://www.youtube.com${u.pathname}${u.search}`;
        }
      }
    } catch (e) {
      // ignore
    }
    return url;
  };

  useEffect(() => {
    (async () => {
      try {
        // Charger les blogs
        const items = await publicBlogService.getPublicBlogs(12);
        setBlogs(Array.isArray(items) ? items : []);
        
        // Charger les publicités actives
        const adResponse = await advertisementService.getActiveAdvertisements();
        setAdvertisements(adResponse || []);
      } catch (e) {
        setBlogs([]);
        setAdvertisements([]);
      }
    })();
  }, []);

  // Styles pour les overlays des partenaires et projets
  const partnerStyles = `
    .partner-card {
      position: relative;
      overflow: hidden;
      border-radius: 12px;
      transition: all 0.3s ease;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      height: 120px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
    }
    .partner-card img {
      max-width: 100%;
      max-height: 80px;
      width: auto;
      height: auto;
      object-fit: contain;
      filter: grayscale(20%);
      transition: filter 0.3s ease;
    }
    .partner-card:hover img {
      filter: grayscale(0%);
    }
    @media (max-width: 768px) {
      .partner-card { height: 90px; padding: 12px; }
      .partner-card img { max-height: 60px; }
    }

    .partner-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }
    
    .partner-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: all 0.3s ease;
      border-radius: 12px;
    }
    
    .partner-card:hover .partner-overlay {
      opacity: 1;
    }
    
    .partner-link {
      color: white;
      text-decoration: none;
      padding: 12px 24px;
      background: linear-gradient(45deg, #667eea, #764ba2);
      border-radius: 25px;
      font-weight: 500;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    }
    
    .partner-link:hover {
      transform: scale(1.05);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
      color: white;
    }
    
    .project-overlay {
      opacity: 0;
    }
    
    .ant-card:hover .project-overlay {
      opacity: 1;
    }
    
    .ant-card:hover .ant-image {
      transform: scale(1.05);
    }
  `;

  // Fonction pour naviguer vers les produits avec la catégorie sélectionnée
  const handleCategoryClick = (categoryName) => {
    // Mapper les noms d'affichage vers les slugs de catégories
    const categorySlugs = {
      'Détection incendie': 'detection-incendie',
      'Éclairage sécurité': 'eclairage-securite',
      'Onduleurs': 'onduleurs',
      'Extinction automatique': 'extinction-automatique',
      'Contrôle d\'accès': 'controle-acces',
      'Vidéosurveillance': 'videosurveillance',
      'Alarmes intrusion': 'alarmes-intrusion',
      'Réseaux informatiques': 'reseaux-informatiques',
      'Interphonie': 'interphonie',
      'DOMOTIQUE/ GRMS': 'grms',
      'GRMS': 'grms',
      'Audio professionnel': 'audio-professionnel',
      'Détection de gaz': 'detection-de-gaz'
    };
    
    const categorySlug = categorySlugs[categoryName];
    if (categorySlug) {
      navigate(`/produits?category=${categorySlug}`);
    } else {
      navigate('/produits');
    }
  };

  const submitSearch = () => {
    const q = (search || '').trim();
    const params = new URLSearchParams();
    
    if (q) {
      params.set('search', q);
    }
    
    if (homeCategory) {
      params.set('category', homeCategory);
    }
    
    const qs = params.toString();
    navigate(qs ? `/produits?${qs}` : '/produits');
  };

  const onEnter = (e) => {
    if (e.key === 'Enter') submitSearch();
  };
  
  // Publicités depuis l'admin, ou placeholder si aucune
  const safeAdvertisements = Array.isArray(advertisements) ? advertisements : [];
  const hasAds = safeAdvertisements.length > 0;

  return (
    <div className="home">
      <style>{partnerStyles}</style>
      {/* Promo Slider Section */}
      <section className="hero-compact mt-header">
        <div className="container">
          {hasAds ? (
            <Slider slides={safeAdvertisements} autoPlay={true} autoPlayDelay={7000} />
          ) : (
            <div className="sld-placeholder">
              <div className="sld-placeholder-content">
                <i className="fas fa-image"></i>
                <h2><span style={{color:'#54ba66'}}>D</span>OMO<span style={{color:'#54ba66'}}>E</span>LEC</h2>
                <p>Votre partenaire en solutions électriques professionnelles</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Statistics Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ color: '#54ba66' }}>
                <i className="fas fa-tools"></i>
              </div>
              <div className="stat-text">
                <h3>500+</h3>
                <p>Installations réalisées</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ color: '#54ba66' }}>
                <i className="fas fa-award"></i>
              </div>
              <div className="stat-text">
                <h3>+13 ans</h3>
                <p>D'expérience</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ color: '#54ba66' }}>
                <i className="fas fa-clock"></i>
              </div>
              <div className="stat-text">
                <h3>24h</h3>
                <p>Intervention d'urgence</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ color: '#54ba66' }}>
                <i className="fas fa-boxes"></i>
              </div>
              <div className="stat-text">
                <h3>+2000</h3>
                <p>Produits</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="main-content">
        <div className="container">
          <div className="content-grid">
            {/* Products Section - Nouveau style */}
            <div className="products-section-modern">
              <div className="section-head">
                <h2>Nos produits</h2>
                <a className="link-see-all" href="/produits">Voir tout</a>
              </div>
              <div className="products-modern-grid">
                {[
                  { icon: 'fas fa-fire', label: 'Détection incendie', color: '#54ba66' },
                  { icon: 'fas fa-bolt', label: 'Éclairage sécurité', color: '#54ba66' },
                  { icon: 'fas fa-car-battery', label: 'Onduleurs', color: '#54ba66' },
                  { icon: 'fas fa-spray-can', label: 'Extinction automatique', color: '#54ba66' },
                  { icon: 'fas fa-fingerprint', label: "Contrôle d'accès", color: '#54ba66' },
                  { icon: 'fas fa-camera', label: 'Vidéosurveillance', color: '#54ba66' },
                  { icon: 'fas fa-shield-alt', label: 'Alarmes intrusion', color: '#54ba66' },
                  { icon: 'fas fa-project-diagram', label: 'Réseaux informatiques', color: '#54ba66' },
                  { icon: 'fas fa-phone-alt', label: 'Interphonie', color: '#54ba66' },
                  { icon: 'fas fa-building', label: 'DOMOTIQUE/ GRMS', color: '#54ba66' },
                  { icon: 'fas fa-headphones-alt', label: 'Audio professionnel', color: '#54ba66' },
                  { icon: 'fas fa-smog', label: 'Détection de gaz', color: '#54ba66' }
                ].map((c) => (
                  <div 
                    key={c.label} 
                    className="product-card-modern" 
                    onClick={() => handleCategoryClick(c.label)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="pc-icon" style={{ background: 'transparent', color: c.color }}>
                      <i className={c.icon}></i>
                    </div>
                    <div className="pc-content">
                      <h3>{c.label}</h3>
                      <span>Découvrir</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tablette Surface style avec vidéo */}
            <div className="surface-promo">
              <div className="surface-wrapper">
                {/* Stand / Pied arrière */}
                <div className="surface-stand"></div>
                {/* Tablette */}
                <div className="surface-device">
                  <div className="surface-bezel">
                    <div className="surface-camera"></div>
                    <div className="surface-screen">
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="surface-video"
                      >
                        <source src="/images/domoelec.mp4" type="video/mp4" />
                      </video>
                      {/* Fallback animé */}
                      <div className="surface-fallback">
                        <div className="surface-fb-glow"></div>
                        <div className="surface-fb-logo">
                          <img src="/images/logofav.png" alt="DOMOELEC" />
                        </div>
                        <h3><span className="hdr-green">D</span>OMO<span className="hdr-green">E</span>LEC</h3>
                        <p>Solutions électriques professionnelles</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Ombre au sol */}
                <div className="surface-shadow"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nos partenaires */}
      <section className="partners-section">
        <div className="container">
          <div className="partners-head">
            <h2>Nos marques partenaires</h2>
          </div>
          <div className="partners-grid">
          
            <div className="partner-card">
              <img src="/images/ZENNIO.png"  />
           
            </div>
            <div className="partner-card">
              <img src="/images/hikvision.png"  />
           
            </div>
            <div className="partner-card">
              <img src="/images/aguilera.png"  />
           
            </div>
            <div className="partner-card">
              <img src="/images/Cooper.svg"  />
           
            </div>
            <div className="partner-card">
              <img src="/images/eaton.png" alt="Eaton" />
        
            </div>
            <div className="partner-card">
              <img src="/images/SENSITRON.jpg" />
             
            </div>
            <div className="partner-card">
              <img src="/images/legrand.png" alt="Legrand" />
            
            </div>
            <div className="partner-card">
              <img src="/images/NUGELEC.png"  />

            </div>
            <div className="partner-card">
              <img src="/images/hdl.webp" alt="HDL" />
            </div>
          </div>
        </div>
      </section>

      {/* Blog vidéo */}
      <section className="video-blog-section" style={{ padding: '50px 0',paddingBottom: '40px' }}>
        <div className="container">
          <div className="section-head">
            <h2>Blog vidéo</h2>
          </div>
          {(() => {
            const extractYtId = (url) => {
              if (!url) return null;
              try {
                const u = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
                if (/youtu\.be$/i.test(u.hostname)) return u.pathname.replace('/', '') || null;
                if (u.pathname.startsWith('/shorts/')) return u.pathname.split('/')[2] || null;
                if (u.pathname.startsWith('/embed/')) return u.pathname.split('/')[2] || null;
                if (u.searchParams.get('v')) return u.searchParams.get('v');
              } catch (_) {}
              return null;
            };

            const videoBlogs = (blogs || [])
              .map(b => ({ ...b, ytId: extractYtId(b.videoUrl), embedUrl: toEmbedUrl(b.videoUrl) }))
              .filter(b => b.ytId || b.embedUrl);
            if (!videoBlogs.length) return null;

            // Vignette cliquable. Détecte automatiquement le placeholder YouTube
            // (si la vidéo n'a pas de miniature uploadée) et affiche une carte stylée.
            const VideoFrame = ({ src, title, ytId }) => {
              const watchUrl = ytId ? `https://www.youtube.com/watch?v=${ytId}` : src;
              const [thumbSrc, setThumbSrc] = React.useState(
                ytId ? `https://i.ytimg.com/vi/${ytId}/maxresdefault.jpg` : null
              );
              const [isPlaceholder, setIsPlaceholder] = React.useState(false);

              const handleLoad = (e) => {
                // Le placeholder YouTube est 120x90, les vraies miniatures >= 480x360
                const w = e.currentTarget.naturalWidth;
                const h = e.currentTarget.naturalHeight;
                if (w > 0 && w < 200) {
                  setIsPlaceholder(true);
                }
              };

              const handleError = (e) => {
                if (!ytId) {
                  setIsPlaceholder(true);
                  return;
                }
                const retries = [
                  `https://i.ytimg.com/vi/${ytId}/sddefault.jpg`,
                  `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`,
                  `https://i.ytimg.com/vi/${ytId}/mqdefault.jpg`,
                ];
                const attempt = parseInt(e.currentTarget.dataset.retry || '0', 10);
                if (attempt < retries.length) {
                  e.currentTarget.dataset.retry = String(attempt + 1);
                  setThumbSrc(retries[attempt]);
                } else {
                  setIsPlaceholder(true);
                }
              };

              return (
                <a
                  href={watchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    position: 'relative',
                    display: 'block',
                    width: '100%',
                    paddingTop: '56.25%',
                    borderRadius: 12,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                    background: isPlaceholder
                      ? 'linear-gradient(135deg, #002c3e 0%, #0a3a50 60%, #10b981 100%)'
                      : '#000',
                    textDecoration: 'none',
                  }}
                  aria-label={`Regarder ${title} sur YouTube`}
                >
                  {thumbSrc && !isPlaceholder && (
                    <img
                      src={thumbSrc}
                      alt={title}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onLoad={handleLoad}
                      onError={handleError}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  )}

                  {isPlaceholder && (
                    <>
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          padding: '24px 24px 80px 24px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <span
                          style={{
                            color: 'rgba(255,255,255,0.75)',
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 1,
                            textTransform: 'uppercase',
                            marginBottom: 6,
                          }}
                        >
                          DOMOELEC · Vidéo
                        </span>
                        <span
                          style={{
                            color: '#fff',
                            fontSize: 18,
                            fontWeight: 700,
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {title}
                        </span>
                      </div>
                      <i
                        className="fab fa-youtube"
                        style={{
                          position: 'absolute',
                          top: 20,
                          right: 20,
                          color: 'rgba(255,255,255,0.22)',
                          fontSize: 60,
                        }}
                      />
                    </>
                  )}

                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background: 'rgba(255, 0, 0, 0.92)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                    }}
                  >
                    <i
                      className="fas fa-play"
                      style={{ color: '#fff', fontSize: 26, marginLeft: 4 }}
                    />
                  </div>

                  <span
                    style={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      background: 'rgba(0, 0, 0, 0.7)',
                      color: '#fff',
                      fontSize: 11,
                      padding: '3px 8px',
                      borderRadius: 4,
                      fontWeight: 500,
                    }}
                  >
                    <i className="fab fa-youtube" style={{ marginRight: 4 }} />
                    YouTube
                  </span>
                </a>
              );
            };

            if (videoBlogs.length <= 3) {
              return (
                <Row gutter={[24, 24]}>
                  {videoBlogs.map(v => (
                    <Col key={v._id} xs={24} md={12} lg={8}>
                      <Card
                        hoverable
                        bodyStyle={{ padding: 16 }}
                        style={{ borderRadius: 12, display: 'flex', flexDirection: 'column' }}
                        cover={<VideoFrame src={v.embedUrl} title={v.title} ytId={v.ytId} />}
                      >
                        <h3 style={{ margin: 0, fontSize: 16, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 48 }}>{v.title}</h3>
                      </Card>
                    </Col>
                  ))}
                </Row>
              );
            }

            // Plus de 3 vidéos → slider auto sans flèches (3 cartes par slide)
            const chunk = (arr, size) => arr.reduce((acc, _, i) => (i % size === 0 ? [...acc, arr.slice(i, i + size)] : acc), []);
            const slides = chunk(videoBlogs, 3);
            return (
              <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <Carousel autoplay autoplaySpeed={4000} dots={false} infinite pauseOnHover draggable>
                  {slides.map((group, idx) => (
                    <div key={idx}>
                      <Row gutter={[24, 24]}>
                        {group.map(v => (
                          <Col key={v._id} xs={24} md={12} lg={8}>
                            <Card
                              hoverable
                              bodyStyle={{ padding: 16 }}
                              style={{ borderRadius: 12, display: 'flex', flexDirection: 'column' }}
                              cover={<VideoFrame src={v.embedUrl} title={v.title} ytId={v.ytId} />}
                            >
                              <h3 style={{ margin: 0, fontSize: 16, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 48 }}>{v.title}</h3>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ))}
                </Carousel>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Nos projets - Album d'images */}
      <section style={{ padding: '50px 0', background: '#fff' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontSize: '2.2rem', fontWeight: 800, marginBottom: 12, color: '#0f172a' }}>Nos <span className="hdr-green">Projets</span></h2>
            <p style={{ fontSize: '1rem', color: '#64748b', maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}>
              Découvrez nos réalisations et installations dans le domaine du courant faible
            </p>
          </div>

          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <Carousel autoplay autoplaySpeed={4000} dots={false} infinite pauseOnHover draggable effect="fade" vertical>
              {(() => {
                const projects = [
                  { id: 1, src: '/images/COMPUS_DES_MINES_VERTES.jpeg', title: 'Campus des Mines Vertes' },
                  { id: 2, src: '/images/Leoni.png', title: 'Projet Leoni' },
                  { id: 4, src: '/images/MYWAY.jpg', title: 'MyWay Project' },
                  { id: 5, src: '/images/NRO.jpg', title: 'NRO Installation' },
                  { id: 6, src: '/images/ONOMO_MASSIRA.jpg', title: 'Onomo Massira' },
                  { id: 7, src: '/images/PARCELLE_O.webp', title: 'Parcelle O' },
                  { id: 8, src: '/images/RELEX_HOTEL.png', title: 'Relex Hotel' },
                  { id: 9, src: '/images/SEWS.jpg', title: 'SEWS Project' },
                  { id: 10, src: '/images/TERMINUS.jpg', title: 'Terminus' },
                  { id: 11, src: '/images/THE_VIEW.jpeg', title: 'The View' }
                ];

                const chunk = (arr, size) => arr.reduce((acc, _, i) => (i % size === 0 ? [...acc, arr.slice(i, i + size)] : acc), []);
                const slides = chunk(projects, 3);

                return slides.map((group, slideIndex) => (
                  <div key={slideIndex}>
                    <Row gutter={[24, 24]}>
                      {group.map(project => (
                        <Col key={project.id} xs={24} md={12} lg={8}>
                          <div
                            style={{
                              position: 'relative',
                              borderRadius: 16,
                              overflow: 'hidden',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              background: '#fff'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-8px)';
                              e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                            }}
                          >
                            <div style={{ position: 'relative', paddingTop: '75%', overflow: 'hidden' }}>
                              <img
                                src={encodeURI(project.src)}
                                alt={project.title}
                                style={{
                                  position: 'absolute',
                                  top: 0, left: 0,
                                  width: '100%', height: '100%',
                                  objectFit: 'cover',
                                  transition: 'transform 0.3s ease'
                                }}
                                onError={(e) => { e.currentTarget.src = '/images/1.jpg'; }}
                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                              />
                              <div style={{
                                position: 'absolute',
                                bottom: 0, left: 0, right: 0,
                                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                                padding: '20px 16px 16px',
                                color: 'white'
                              }}>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                                  {project.title}
                                </h3>
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                ));
              })()}
            </Carousel>
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="rv-section">
        <div className="container">
          <div className="rv-header">
            <h2>Ils parlent de <span className="hdr-green">D</span>OMO<span className="hdr-green">E</span>LEC</h2>
          </div>
          {(() => {
            const reviews = [
              { id: 1, name: "Anas", initial: "A", date: "20/08/2024", note: 5, text: "Toujours au top, le site est intuitif et fonctionne de manière remarquable. BRAVO." },
              { id: 2, name: "Mohamed", initial: "M", date: "12/03/2024", note: 5, text: "Le site est bien construit, les produits se trouvent facilement. Rapide, efficace." },
              { id: 3, name: "Anissa", initial: "A", date: "10/07/2024", note: 5, text: "Le moteur de recherche fonctionne de mieux en mieux." },
              { id: 4, name: "Imane", initial: "I", date: "18/06/2024", note: 5, text: "Simple, rapide et efficace." },
              { id: 5, name: "Rachid", initial: "R", date: "02/05/2024", note: 5, text: "Equipe très pro, conseils pertinents et disponibilité." },
            ];
            const rvRef = React.createRef();
            const scrollRv = (dir) => {
              if (rvRef.current) rvRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
            };
            return (
              <>
                <div className="rv-controls">
                  <button onClick={() => scrollRv(-1)} aria-label="Précédent"><i className="fas fa-arrow-left"></i></button>
                  <button onClick={() => scrollRv(1)} aria-label="Suivant"><i className="fas fa-arrow-right"></i></button>
                </div>
                <div className="rv-track" ref={rvRef}>
                  {reviews.map((r) => (
                    <article key={r.id} className="rv-card">
                      <div className="rv-stars">{"★".repeat(r.note)}</div>
                      <p className="rv-text">"{r.text}"</p>
                      <div className="rv-author">
                        <div className="rv-avatar">{r.initial}</div>
                        <div>
                          <strong className="rv-name">{r.name}</strong>
                          <span className="rv-date">{r.date}</span>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </section>
      <Newsletter />
    </div>
  );
};

export default Home;